import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { partnerService, ReceivedInvitation } from '@/services/partner';
import { toast } from 'react-hot-toast';
import { getStatusColor } from '@/types/enums';

export const ReceivedInvitationsPage: React.FC = () => {
  const { t } = useLanguage();
  const [invitations, setInvitations] = useState<ReceivedInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingInvitation, setProcessingInvitation] = useState<string | null>(null);

  useEffect(() => {
    loadInvitations();
  }, []);

  const loadInvitations = async () => {
    try {
      setLoading(true);
      const data = await partnerService.getReceivedInvitations();
      
      // Debug: Log sender information for each invitation
      // data.forEach((invitation, index) => {
      // });
            
      setInvitations(data);
    } catch (err) {
      console.error('Error loading received invitations:', err);
      toast.error(t('partner.failedToLoadInvitations'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (invitationId: string, status: 'ACCEPTED' | 'CANCELLED') => {
    try {
      setProcessingInvitation(invitationId);
      await partnerService.updateInvitationStatus(invitationId, status);
      
      // Reload invitations to get updated status
      await loadInvitations();
      
      // Show success message
      toast.success(status === 'ACCEPTED' ? t('partner.invitationAcceptedSuccessfully') : t('partner.invitationDeclinedSuccessfully'));
    } catch (err) {
      console.error(`Error ${status.toLowerCase()}ing invitation:`, err);
             toast.error(status === 'ACCEPTED' ? t('partner.failedToAcceptInvitation') : t('partner.failedToDeclineInvitation'));
    } finally {
      setProcessingInvitation(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const isExpired = (expiresAt: string) => {
    return new Date() > new Date(expiresAt);
  };

  const getStatusDisplay = (invitation: ReceivedInvitation) => {
    if (invitation.status === 'PENDING' && isExpired(invitation.expires_at)) {
      return { status: 'EXPIRED', color: 'red' };
    }
    return { status: invitation.status, color: getStatusColor(invitation.status) };
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
                     <div className="text-[var(--color-secondary-text)]">{t('partner.loadingInvitations')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
             <div className="mb-6">
         <h1 className="text-2xl font-bold text-[var(--color-surface-text)]">{t('partner.receivedInvitations')}</h1>
         <p className="text-[var(--color-secondary-text)] mt-2">
           {t('partner.receivedInvitationsDescription')}
         </p>
       </div>

      <div className="bg-[var(--color-surface-bg)] rounded-lg shadow border border-[var(--color-border)]">
                 <div className="p-6 border-b border-[var(--color-border)]">
           <h2 className="text-lg font-semibold text-[var(--color-surface-text)]">{t('partner.receivedInvitations')} ({invitations.length})</h2>
         </div>
         
         {invitations.length === 0 ? (
           <div className="p-6 text-center text-[var(--color-secondary-text)]">
             {t('partner.noReceivedInvitations')}
           </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[var(--color-border)]">
              <thead className="bg-[var(--color-secondary-bg)]">
                <tr>
                                     <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase">{t('partner.fromOrganization')}</th>
                   <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase">{t('partner.sentBy')}</th>
                   <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase">{t('partner.status')}</th>
                   <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase">{t('partner.received')}</th>
                   <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase">{t('partner.expires')}</th>
                   <th className="px-6 py-3 text-right text-xs font-medium text-[var(--color-secondary-text)] uppercase">{t('partner.actions')}</th>
                </tr>
              </thead>
              <tbody className="bg-[var(--color-surface-bg)] divide-y divide-[var(--color-border)]">
                {invitations.map((invitation) => {
                  const statusDisplay = getStatusDisplay(invitation);
                  const canTakeAction = invitation.status === 'PENDING' && !isExpired(invitation.expires_at);
                  
                  return (
                    <tr key={invitation.id} className="hover:bg-[var(--color-secondary-bg)]">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-surface-text)]">
                                                 {invitation.sender_tenant_name || t('partner.unknownOrganization')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-surface-text)]">
                        {invitation.sender_first_name && invitation.sender_last_name 
                          ? `${invitation.sender_first_name} ${invitation.sender_last_name} (${invitation.sender_email})`
                          : invitation.sender_first_name 
                          ? `${invitation.sender_first_name} (${invitation.sender_email})`
                          : invitation.sender_last_name
                          ? `${invitation.sender_last_name} (${invitation.sender_email})`
                          : invitation.sender_email && invitation.sender_email !== 'Unknown'
                          ? invitation.sender_email
                                                     : t('partner.unknownSender')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          statusDisplay.color === 'red' 
                            ? 'bg-[var(--color-error)]/10 text-[var(--color-error)]'
                            : statusDisplay.color === 'green'
                            ? 'bg-[var(--color-success)]/10 text-[var(--color-success)]'
                            : statusDisplay.color === 'yellow'
                            ? 'bg-[var(--color-warning)]/10 text-[var(--color-warning)]'
                            : 'bg-[var(--color-border)] text-[var(--color-secondary-text)]'
                        }`}>
                          {statusDisplay.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-surface-text)]">
                        {formatDate(invitation.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-surface-text)]">
                        {formatDate(invitation.expires_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {canTakeAction ? (
                          <div className="flex space-x-2 justify-end">
                            <button
                              onClick={() => handleUpdateStatus(invitation.id, 'ACCEPTED')}
                              disabled={processingInvitation === invitation.id}
                              className="text-green-600 hover:text-green-900 disabled:opacity-50"
                            >
                                                             {processingInvitation === invitation.id ? t('partner.processing') : t('partner.accept')}
                            </button>
                            <button
                              onClick={() => handleUpdateStatus(invitation.id, 'CANCELLED')}
                              disabled={processingInvitation === invitation.id}
                              className="text-red-600 hover:text-red-900 disabled:opacity-50"
                            >
                                                             {processingInvitation === invitation.id ? t('partner.processing') : t('partner.decline')}
                            </button>
                          </div>
                        ) : (
                                                     <span className="text-gray-400">{t('partner.noActionsAvailable')}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
